import React from "react"
import { useQuery } from "@apollo/client/react"
import {
  OrderWorkflowSearchHit,
  OrderWorkflowTrigger,
  TriggersDocument,
  TriggersQuery,
  TriggersQueryVariables,
  SearchOrderWorkflowsDocument,
  SearchOrderWorkflowsQuery,
  SearchOrderWorkflowsQueryVariables,
  SearchExecutionCountsDocument,
  SearchExecutionCountsQuery,
  SearchExecutionCountsQueryVariables,
  OrderWorkflowTriggerType,
  SortOrder
} from "lib/types/generated/graphql"
import { useNavigate } from "react-router-dom"

import PageHeader from "components/Ui/Page/PageHeader"
import WorkflowTable from "components/Workflow/WorkflowTable"
import { WithAuth } from "components/Auth"
import { PermissionGroups } from "routes/constants"

import { ReactComponent as PlusIcon } from "images/icons/plus.svg"
import { Container, StyledButton } from "./ShowWorkflows.styled"

const SIZE = 100

export const ShowWorkflows = () => {
  const navigate = useNavigate()

  const { data, loading, error } = useQuery<
    SearchOrderWorkflowsQuery,
    SearchOrderWorkflowsQueryVariables
  >(SearchOrderWorkflowsDocument, {
    variables: {
      from: 0,
      size: SIZE,
      sort: [{ field: "updated", order: SortOrder.Desc }]
    }
  })

  const { data: triggersData, loading: triggersLoading } = useQuery<
    TriggersQuery,
    TriggersQueryVariables
  >(TriggersDocument, {
    variables: { workflowTriggerType: OrderWorkflowTriggerType.OrderCreated }
  })

  const publishedMap = new Map<string, number>(
    (triggersData?.Order?.workflowTriggers?.triggers ?? []).map(
      (t: OrderWorkflowTrigger) => [t.id, t.revision] as [string, number]
    )
  )

  const executionCountsAggs = JSON.stringify({
    by_workflow: {
      terms: { field: "workflowId", size: 100 },
      aggs: {
        completed: { filter: { exists: { field: "completed" } } },
        failed: { filter: { exists: { field: "failed" } } }
      }
    }
  })

  const { data: countsData, loading: countsLoading } = useQuery<
    SearchExecutionCountsQuery,
    SearchExecutionCountsQueryVariables
  >(SearchExecutionCountsDocument, {
    variables: { from: 0, size: 0, aggs: executionCountsAggs }
  })

  const executionCounts = new Map<string, { total: number; completed: number; failed: number }>()
  if (countsData?.searchOrderWorkflowExecutions?.aggregations) {
    const aggs =
      typeof countsData.searchOrderWorkflowExecutions.aggregations === "string"
        ? JSON.parse(countsData.searchOrderWorkflowExecutions.aggregations)
        : countsData.searchOrderWorkflowExecutions.aggregations
    const buckets = aggs?.by_workflow?.buckets ?? []
    for (const bucket of buckets) {
      executionCounts.set(bucket.key, {
        total: bucket.doc_count,
        completed: bucket.completed?.doc_count ?? 0,
        failed: bucket.failed?.doc_count ?? 0
      })
    }
  }

  const workflows: OrderWorkflowSearchHit[] = data?.searchOrderWorkflows?.hits ?? []

  return (
    <Container>
      <PageHeader
        title="Workflows"
        description="Order workflows automate actions based on events. Create workflows that trigger on order events, evaluate conditions, and perform actions like cancellations or custom state changes."
      >
        <WithAuth requiredGroups={PermissionGroups.workflows.write}>
          <StyledButton type="button" handleClick={() => navigate("/settings/workflows/create")}>
            <PlusIcon /> Create workflow
          </StyledButton>
        </WithAuth>
      </PageHeader>
      <WorkflowTable
        workflows={workflows}
        loading={loading}
        error={error}
        publishedMap={publishedMap}
        statusLoading={triggersLoading}
        executionCounts={executionCounts}
        countsLoading={countsLoading}
      />
    </Container>
  )
}
